// One way out to the network for the desk. Polymarket has already answered 429 to this
// product, so every request goes through a per-host gate that bounds how fast we ask and
// stops the whole host, not just one call, while a 429 cools down.

export interface HostRule {
	concurrent: number;
	/** Least time between two requests starting. */
	minGapMs: number;
}

const DEFAULT_RULE: HostRule = { concurrent: 4, minGapMs: 0 };

const RULES: Record<string, HostRule> = {
	"gamma-api.polymarket.com": { concurrent: 4, minGapMs: 50 },
	"data-api.polymarket.com": { concurrent: 3, minGapMs: 200 },
	"clob.polymarket.com": { concurrent: 1, minGapMs: 1_000 },
};

const TIMEOUT_MS = 15_000;
const RETRIES = 1;
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 8_000;
/** Longer than this and the caller gets the error, rather than a spinner for a minute. */
const MAX_RETRY_WAIT_MS = 30_000;
/** Each failure in a row holds the whole host back twice as long as the last, up to this. */
const FAIL_HOLD_BASE_MS = 500;
const FAIL_HOLD_MAX_MS = 4_000;
/** After this many in a row, calls fail at once during a hold instead of queueing through it. */
const REFUSE_AFTER_FAILURES = 3;

export class HttpError extends Error {
	constructor(
		message: string,
		readonly status: number,
		/** How long the server asked us to stay away, when it did. */
		readonly retryAfterMs: number | null = null,
	) {
		super(message);
		this.name = "HttpError";
	}
}

const abortError = () => new DOMException("Aborted", "AbortError");

export const isAbort = (e: unknown): boolean => (e as { name?: string } | null | undefined)?.name === "AbortError";

/** Retry-After as milliseconds, from either of its forms: seconds or an HTTP date (pure). */
export function parseRetryAfter(value: string | null | undefined, nowMs: number): number | null {
	if (!value) return null;
	const seconds = Number(value);
	if (Number.isFinite(seconds)) return Math.max(0, seconds * 1_000);
	const at = Date.parse(value);
	return Number.isFinite(at) ? Math.max(0, at - nowMs) : null;
}

/** Exponential with jitter, so tabs that failed together do not retry together (pure). */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
	const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** attempt);
	return Math.round(ceiling * (0.5 + random() / 2));
}

export interface Deps {
	fetch: (url: string, init: { signal: AbortSignal }) => Promise<Response>;
	sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
	now: () => number;
	random: () => number;
	rules: Record<string, HostRule>;
}

const realSleep = (ms: number, signal?: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		if (signal?.aborted) {
			reject(abortError());
			return;
		}
		const done = () => {
			signal?.removeEventListener("abort", stop);
			resolve();
		};
		const t = setTimeout(done, ms);
		const stop = () => {
			clearTimeout(t);
			reject(abortError());
		};
		signal?.addEventListener("abort", stop, { once: true });
	});

interface Waiter {
	resolve: () => void;
	reject: (e: unknown) => void;
	signal: AbortSignal;
}

class Gate {
	private active = 0;
	private nextAt = 0;
	private blockedUntil = 0;
	private failures = 0;
	private pumping = false;
	private readonly queue: Waiter[] = [];

	constructor(
		private readonly rule: HostRule,
		private readonly deps: Deps,
	) {}

	acquire(signal: AbortSignal): Promise<void> {
		return new Promise((resolve, reject) => {
			if (signal.aborted) {
				reject(abortError());
				return;
			}
			const refused = this.refusal();
			if (refused) {
				reject(refused);
				return;
			}
			const waiter = { resolve, reject, signal };
			this.queue.push(waiter);
			signal.addEventListener(
				"abort",
				() => {
					const at = this.queue.indexOf(waiter);
					if (at >= 0) this.queue.splice(at, 1);
					reject(abortError());
				},
				{ once: true },
			);
			void this.pump();
		});
	}

	release(): void {
		this.active -= 1;
		void this.pump();
	}

	/** Stay away until then. A wait too long to sit through fails everything queued now. */
	block(untilMs: number): void {
		this.blockedUntil = Math.max(this.blockedUntil, untilMs);
		const refused = this.refusal();
		if (refused) for (const w of this.queue.splice(0)) w.reject(refused);
	}

	/** The host is failing: back off for everyone, whichever call happens to hit it next. */
	failed(): void {
		const hold = Math.min(FAIL_HOLD_MAX_MS, FAIL_HOLD_BASE_MS * 2 ** this.failures);
		this.failures += 1;
		this.block(this.deps.now() + hold);
	}

	succeeded(): void {
		this.failures = 0;
	}

	private refusal(): HttpError | null {
		const left = this.blockedUntil - this.deps.now();
		if (left > MAX_RETRY_WAIT_MS) return new HttpError("host asked us to wait", 429, left);
		if (left > 0 && this.failures >= REFUSE_AFTER_FAILURES) return new HttpError("host is failing", 503, left);
		return null;
	}

	private async pump(): Promise<void> {
		if (this.pumping) return;
		this.pumping = true;
		while (this.queue.length > 0 && this.active < this.rule.concurrent) {
			const wait = Math.max(this.nextAt, this.blockedUntil) - this.deps.now();
			if (wait > 0) {
				await this.deps.sleep(wait);
				continue;
			}
			const next = this.queue.shift();
			if (!next || next.signal.aborted) continue;
			this.active += 1;
			this.nextAt = this.deps.now() + this.rule.minGapMs;
			next.resolve();
		}
		this.pumping = false;
	}
}

export interface FetchOptions {
	signal?: AbortSignal;
	timeoutMs?: number;
	retries?: number;
}

interface InFlight {
	promise: Promise<unknown>;
	ctl: AbortController;
	waiters: number;
}

export function createClient(overrides: Partial<Deps> = {}) {
	const deps: Deps = {
		fetch: (url, init) => fetch(url, init),
		sleep: realSleep,
		now: () => Date.now(),
		random: Math.random,
		rules: RULES,
		...overrides,
	};
	const gates = new Map<string, Gate>();
	const inflight = new Map<string, InFlight>();

	const gateFor = (host: string): Gate => {
		let g = gates.get(host);
		if (!g) {
			g = new Gate(deps.rules[host] ?? DEFAULT_RULE, deps);
			gates.set(host, g);
		}
		return g;
	};

	/** One attempt, holding a slot for its whole length, body included. */
	async function attempt(url: string, gate: Gate, outer: AbortSignal, timeoutMs: number, n: number): Promise<Response & { json: () => Promise<unknown>; waitMs: number }> {
		await gate.acquire(outer);
		const ctl = new AbortController();
		const onOuter = () => ctl.abort();
		outer.addEventListener("abort", onOuter, { once: true });
		let timedOut = false;
		const timer = setTimeout(() => {
			timedOut = true;
			ctl.abort();
		}, timeoutMs);
		try {
			const res = await deps.fetch(url, { signal: ctl.signal });
			const text = res.ok ? await res.text() : "";
			// Here, not in the caller: the finally below lets the next request out, and that one
			// must already see what the host just said.
			const waitMs = res.status === 429 ? (parseRetryAfter(res.headers.get("retry-after"), deps.now()) ?? backoffMs(n, deps.random)) : 0;
			if (res.status === 429) gate.block(deps.now() + waitMs);
			else if (res.status >= 500) gate.failed();
			else if (res.ok) gate.succeeded();
			return Object.assign(res, { json: () => Promise.resolve(JSON.parse(text) as unknown), waitMs });
		} catch (e) {
			if (outer.aborted) throw abortError();
			if (e instanceof SyntaxError) throw e;
			gate.failed();
			if (timedOut) throw new HttpError(`timed out after ${timeoutMs}ms`, 0);
			throw e;
		} finally {
			clearTimeout(timer);
			outer.removeEventListener("abort", onOuter);
			gate.release();
		}
	}

	async function run<T>(url: string, signal: AbortSignal, timeoutMs: number, retries: number): Promise<T> {
		const { hostname } = new URL(url);
		const gate = gateFor(hostname);
		for (let n = 0; ; n++) {
			let res: Awaited<ReturnType<typeof attempt>>;
			try {
				res = await attempt(url, gate, signal, timeoutMs, n);
			} catch (e) {
				// A timeout has already cost its full wait. Say so now: three in a row is most of a
				// minute of blank screen, and the polling above this keeps trying regardless.
				if (isAbort(e) || e instanceof HttpError || n >= retries) throw e;
				await deps.sleep(backoffMs(n, deps.random), signal);
				continue;
			}
			if (res.ok) return (await res.json()) as T;
			if (res.status === 429) {
				if (n >= retries || res.waitMs > MAX_RETRY_WAIT_MS) throw new HttpError(`${hostname} 429`, 429, res.waitMs);
				continue;
			}
			if (res.status >= 500 && n < retries) {
				await deps.sleep(backoffMs(n, deps.random), signal);
				continue;
			}
			throw new HttpError(`${hostname} ${res.status}`, res.status);
		}
	}

	function fetchJson<T = unknown>(url: string, opts: FetchOptions = {}): Promise<T> {
		const { signal, timeoutMs = TIMEOUT_MS, retries = RETRIES } = opts;
		if (signal?.aborted) return Promise.reject(abortError());
		let entry = inflight.get(url);
		if (!entry || entry.ctl.signal.aborted) {
			const ctl = new AbortController();
			const promise = run<T>(url, ctl.signal, timeoutMs, retries);
			const made: InFlight = { promise, ctl, waiters: 0 };
			entry = made;
			inflight.set(url, made);
			const clear = () => {
				if (inflight.get(url) === made) inflight.delete(url);
			};
			promise.then(clear, clear);
		}
		const shared = entry;
		shared.waiters += 1;
		if (!signal) return shared.promise as Promise<T>;
		// A shared request ends early only when everyone waiting on it has gone.
		return new Promise<T>((resolve, reject) => {
			const leave = () => {
				shared.waiters -= 1;
				if (shared.waiters === 0) shared.ctl.abort();
				reject(abortError());
			};
			signal.addEventListener("abort", leave, { once: true });
			const finish = () => signal.removeEventListener("abort", leave);
			(shared.promise as Promise<T>).then(
				(v) => {
					finish();
					resolve(v);
				},
				(e) => {
					finish();
					reject(e);
				},
			);
		});
	}

	return { fetchJson };
}

export const { fetchJson } = createClient();
